import type { FeatureData } from "@/types/spectrum";
import { getCategoryLabel } from "@/utils/compoundCategory";
import {
  aggregateCategoriesByObservation,
  countObservations,
} from "@/utils/observationGrouping";

/**
 * FactSummaryCard — 팩트 기반 화합물 검출 요약 카드 (S23-E).
 *
 * 경찰청 미팅 결정: "AI가 방화 여부를 결정하면 안 됨. 인화성 물질 포함
 * 여부(有無)만 팩트로."
 *
 * 도출 규칙:
 *   - 인화성 물질 관련 화합물: features 중 category ∈ {petroleum_alkane(유류계 알칸),
 *     aromatic(방향족)} → 휘발유·등유·경유 및 BTEX 지표 물질군.
 *   - 테러 의심 물질: 판정 기준(물질 DB) 미비 → "판정 기준 준비 중" 명시.
 *   - 음독 관련 물질: 판정 기준(물질 DB) 미비 → "판정 기준 준비 중" 명시.
 *
 * caveat: 검출된 화합물 팩트이며, 방화 등 최종 판단은 수사기관·국과수 종합 영역.
 *
 * T1 (2026-08-20): 건수는 **관찰(스캔) 단위**다. 같은 스캔에서 나온 AI 후보
 * 여러 개(Ethylbenzene·Xylene 3종…)를 "방향족 5건" 으로 세면 증거가 인위적으로
 * 중복된다(기획 v4 §3-①). 후보 수는 따로 밝힌다. "검출" 대신 "후보" 라고 부른다 —
 * 어느 보조 검색도 단독으로 물질을 동정하지 않는다.
 */

interface FactSummaryCardProps {
  features: FeatureData[];
}

/**
 * 인화성 물질 관련 화합물 개수 도출.
 * 기준: petroleum_alkane (유류계 알칸) + aromatic (방향족·자극성).
 */
function deriveFlammableDetection(features: FeatureData[]): {
  petroleumCount: number;
  aromaticCount: number;
  candidateCount: number;
  total: number;
} {
  const agg = aggregateCategoriesByObservation(features);
  const petroleum = agg.get("petroleum_alkane");
  const aromatic = agg.get("aromatic");
  const petroleumCount = petroleum?.observations ?? 0;
  const aromaticCount = aromatic?.observations ?? 0;
  return {
    petroleumCount,
    aromaticCount,
    candidateCount: (petroleum?.candidates ?? 0) + (aromatic?.candidates ?? 0),
    total: petroleumCount + aromaticCount,
  };
}

interface FactRowProps {
  label: string;
  value: string;
  valueVariant?: "detected" | "none" | "pending" | "default";
  detail?: string;
}

function FactRow({
  label,
  value,
  valueVariant = "default",
  detail,
}: FactRowProps) {
  const valueColor: Record<FactRowProps["valueVariant"] & string, string> = {
    detected: "text-orange-700 font-semibold",
    none: "text-green-700",
    pending: "text-gray-400 italic",
    default: "text-gray-700",
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-start gap-3">
        <span className="text-xs font-medium text-gray-500 w-44 shrink-0 pt-0.5">
          {label}
        </span>
        <span className={`text-sm ${valueColor[valueVariant]}`}>{value}</span>
      </div>
      {detail && (
        <span className="text-xs text-gray-400 pl-[11.5rem]">{detail}</span>
      )}
    </div>
  );
}

export default function FactSummaryCard({ features }: FactSummaryCardProps) {
  const { petroleumCount, aromaticCount, candidateCount, total } =
    deriveFlammableDetection(features);

  const hasFlammable = total > 0;

  // 전체 카테고리 집계 — 관찰(스캔) 단위 + 후보 수
  const agg = aggregateCategoriesByObservation(features);
  const { observationCount } = countObservations(features);
  const categoryEntries = Array.from(agg.entries()).filter(
    ([, count]) => count.observations > 0,
  );

  return (
    <div data-testid="fact-summary-card" className="space-y-4">
      {/* 헤더 */}
      <div>
        <h2 className="text-sm font-black uppercase tracking-widest text-gray-500">
          화합물 후보 팩트 요약
        </h2>
        <p className="text-xs text-gray-400 mt-0.5">
          AI 후보 자동 분류 — 건수는 관찰(스캔) 단위, 수사기관·국과수 종합
          판단과 구분
        </p>
      </div>

      {/* 팩트 행 목록 */}
      <div className="space-y-3">
        {/* 1. 인화성 물질 관련 화합물 */}
        <FactRow
          label="인화성 물질 관련 화합물"
          value={
            hasFlammable
              ? `후보 있음 (유류계 알칸 관찰 ${petroleumCount}건 · 방향족 관찰 ${aromaticCount}건)`
              : "후보 없음"
          }
          valueVariant={hasFlammable ? "detected" : "none"}
          detail={
            hasFlammable
              ? `${getCategoryLabel("petroleum_alkane")}(휘발유·등유·경유 지표) + ${getCategoryLabel("aromatic")}(BTEX 등 방향족) · 관찰 ${observationCount}건 중 · 후보 ${candidateCount}건 — 같은 분류의 같은 스캔 후보는 관찰 1건, 한 관찰의 후보가 여러 분류에 걸치면 각 분류에 1건씩`
              : undefined
          }
        />

        {/* 2. 테러 의심 물질 */}
        <FactRow
          label="테러 의심 물질"
          value="판정 기준 준비 중 (물질 DB 연동 예정)"
          valueVariant="pending"
        />

        {/* 3. 음독 관련 물질 */}
        <FactRow
          label="음독 관련 물질"
          value="판정 기준 준비 중 (물질 DB 연동 예정)"
          valueVariant="pending"
        />

        {/* 4. 검출 카테고리별 건수 */}
        {categoryEntries.length > 0 && (
          <div className="flex flex-col gap-0.5">
            <span
              className="text-xs font-medium text-gray-500"
              title="관찰(스캔) 단위 계수. 한 관찰의 후보가 여러 분류에 걸치면(분류 미확정) 각 분류에 1건씩 셉니다 — 합이 총 관찰 수보다 클 수 있습니다."
            >
              후보 카테고리 분포 (관찰 {observationCount}건 중 · 여러 분류에
              걸치면 각 분류에 1건씩)
            </span>
            <div
              data-testid="fact-category-breakdown"
              className="flex flex-wrap gap-2 pt-1"
            >
              {categoryEntries.map(([cat, count]) => (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
                >
                  {getCategoryLabel(cat)}
                  <span className="font-semibold text-gray-900">
                    관찰 {count.observations}건
                  </span>
                  {count.candidates > count.observations && (
                    <span className="text-gray-500">
                      · 후보 {count.candidates}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Caveat */}
      <p
        data-testid="fact-caveat"
        className="text-[11px] text-gray-400 leading-relaxed border-t border-gray-100 pt-3"
      >
        검출된 화합물에 대한 팩트이며, 방화 등 최종 판단은 수사기관·국과수 종합
        영역입니다. 인화성 물질 검출이 방화를 단정하지 않으며, 미검출이 방화를
        배제하지 않습니다.
      </p>
    </div>
  );
}
